import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, SimpleGrid, Spinner,
  Badge, Icon, Divider, Alert, AlertIcon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiCheckCircle, FiXCircle, FiAlertTriangle, FiDatabase, FiZap, FiSearch } from 'react-icons/fi';

const API_BASE = '/api/v1/admin-ui';

function HealthCheck() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Token ${localStorage.getItem('token')}`,
    };
    const instanceId = localStorage.getItem('instance_id');
    if (instanceId) headers['X-Tenant-ID'] = instanceId;
    return headers;
  };

  const fetchHealth = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setHealth(data); setLastChecked(new Date()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const statusColor = (status) => {
    switch (status) {
      case 'healthy': case 'connected': case 'complete': return 'green';
      case 'degraded': case 'incomplete': return 'orange';
      case 'not_configured': return 'yellow';
      case 'error': case 'unhealthy': return 'red';
      default: return 'gray';
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>;
  if (!health) return <Alert status="error"><AlertIcon />Failed to load health check data</Alert>;

  const checks = health.checks || {};

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Health Check</Heading>
          <Text color="gray.500">
            Service connectivity and status
            {lastChecked && ` — last checked ${lastChecked.toLocaleTimeString()}`}
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="orange" onClick={fetchHealth} isLoading={loading}>Re-check</Button>
      </HStack>

      <Alert
        status={health.overall === 'healthy' ? 'success' : health.overall === 'degraded' ? 'warning' : 'error'}
        borderRadius="lg" mb={6} py={4}
      >
        <AlertIcon boxSize={6} />
        <VStack align="start" spacing={0} ml={2}>
          <Text fontWeight="bold" fontSize="lg">
            Overall: <Badge colorScheme={statusColor(health.overall)} fontSize="md" px={2}>{health.overall}</Badge>
          </Text>
        </VStack>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
        {/* Database */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiDatabase} boxSize={6} color="blue.500" />
            <Heading size="md">Database</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.database?.status)} fontSize="sm">{checks.database?.status || 'unknown'}</Badge>
            </HStack>
            {checks.database?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.database.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.database?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.database.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Redis */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiZap} boxSize={6} color="red.500" />
            <Heading size="md">Redis</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.redis?.status)} fontSize="sm">{checks.redis?.status || 'unknown'}</Badge>
            </HStack>
            {checks.redis?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.redis.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.redis?.used_memory_human && (
              <HStack justify="space-between">
                <Text color="gray.600">Memory</Text>
                <Text fontWeight="bold">{checks.redis.used_memory_human}</Text>
              </HStack>
            )}
            {checks.redis?.message && (
              <Alert status={checks.redis.status === 'not_configured' ? 'warning' : 'error'} borderRadius="md" fontSize="sm"><AlertIcon />{checks.redis.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Elasticsearch */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiSearch} boxSize={6} color="yellow.600" />
            <Heading size="md">Elasticsearch</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.elasticsearch?.status)} fontSize="sm">{checks.elasticsearch?.status || 'unknown'}</Badge>
            </HStack>
            {checks.elasticsearch?.latency_ms != null && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="bold">{checks.elasticsearch.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.elasticsearch?.cluster_name && (
              <HStack justify="space-between">
                <Text color="gray.600">Cluster</Text>
                <Text fontWeight="bold">{checks.elasticsearch.cluster_name}</Text>
              </HStack>
            )}
            {checks.elasticsearch?.version && (
              <HStack justify="space-between">
                <Text color="gray.600">Version</Text>
                <Text fontWeight="bold">{checks.elasticsearch.version}</Text>
              </HStack>
            )}
            {checks.elasticsearch?.message && (
              <Alert status={checks.elasticsearch.status === 'not_configured' ? 'warning' : 'error'} borderRadius="md" fontSize="sm"><AlertIcon />{checks.elasticsearch.message}</Alert>
            )}
          </VStack>
        </Box>

        {/* Data Summary */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack mb={4}>
            <Icon as={FiCheckCircle} boxSize={6} color="green.500" />
            <Heading size="md">Data</Heading>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.data?.status)} fontSize="sm">{checks.data?.status || 'unknown'}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Profiles</Text>
              <Text fontWeight="bold">{checks.data?.total_profiles || 0} ({checks.data?.active_profiles || 0} active)</Text>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Webhooks</Text>
              <Text fontWeight="bold">{checks.data?.total_webhooks || 0} ({checks.data?.active_webhooks || 0} active)</Text>
            </HStack>
            {checks.data?.message && (
              <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{checks.data.message}</Alert>
            )}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default HealthCheck;
